import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { useAuth } from "../pages/context/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, setDoc, onSnapshot, collection, query, where, Timestamp } from "firebase/firestore";
import { FaArrowLeft, FaHome, FaSearch, FaBars, FaBell, FaCrown, FaStar, FaLock, FaShieldAlt, FaHeadset, FaSignOutAlt, FaUserCircle, FaTimes } from "react-icons/fa";
import headerImg from "../assets/header.png";

const SUPER_ADMIN_EMAILS = ["sonu8507088241@gmail.com", "sarvagyamurti@gmail.com"];

function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const { currentUser, userRole } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeen, setLastSeen] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const dropdownRef = useRef(null);

  const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(currentUser?.email);

  const recalcUnreadAlerts = (ids) => {
    const saved = currentUser ? localStorage.getItem("sarvagya_read_alerts_" + currentUser.uid) : null;
    const readArray = saved ? JSON.parse(saved) : [];
    setUnreadAlerts(ids.filter(id => !readArray.includes(id)).length);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setLastSeen(data.lastNotificationSeen || null);
          setUserPhoto(data.photoURL || null);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchUserData();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, "notifications"), where("expiresAt", ">", Timestamp.now()));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allNotifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unread = allNotifications.filter(notif => {
        if (!lastSeen) return true;
        const notifTime = notif.createdAt?.toMillis() || 0;
        const seenTime = lastSeen?.toMillis() || 0;
        return notifTime > seenTime;
      });
      setUnreadCount(unread.length);
    });
    return () => unsubscribe();
  }, [currentUser, lastSeen]);

  useEffect(() => {
    if (!currentUser) { setUnreadAlerts(0); return; }
    let alertIds = [];
    const unsub = onSnapshot(collection(db, "alerts"), (snapshot) => {
      const now = new Date();
      const ids = [];
      snapshot.forEach((d) => {
        const data = d.data();
        if (data.expiresAt && data.expiresAt.toDate() < now) return;
        if (!data.hidden) ids.push(d.id);
      });
      alertIds = ids;
      recalcUnreadAlerts(ids);
    });

    const handleReadUpdate = () => recalcUnreadAlerts(alertIds);
    window.addEventListener("sarvagya_alerts_read_updated", handleReadUpdate);

    return () => {
      unsub();
      window.removeEventListener("sarvagya_alerts_read_updated", handleReadUpdate);
    };
  }, [currentUser]);

  const goToNotifications = async () => {
    setShowDropdown(false);
    navigate("/Notifications");
    if (currentUser) {
      const now = Timestamp.now();
      try {
        await setDoc(doc(db, "users", currentUser.uid), { lastNotificationSeen: now }, { merge: true });
        setLastSeen(now);
        setUnreadCount(0);
      } catch (err) {}
    }
  };

  const handleLogout = () => {
    setShowDropdown(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    signOut(auth).then(() => navigate("/login")).catch((err) => console.error(err));
  };

  const isHome = location.pathname === "/" || location.pathname === "/home";

  return (
    <>
      <div className="sticky top-0 z-50 w-full h-20 shadow-lg">
        <img src={headerImg} alt="Sarvagya Header" className="absolute inset-0 w-full h-full object-cover z-0" />
        <div className="relative z-10 flex items-center justify-between w-full h-full px-4 md:px-6">

          {isHome ? (
            <div className="flex items-center gap-3">
              <div onClick={() => navigate("/SarvagyaConnect?section=alerts")} className="flex flex-col items-center justify-center cursor-pointer text-white">
                <div className="flex items-center justify-center w-10 h-10 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition shadow-md border border-white/30 mb-1 relative">
                  <FaBell size={16} />
                  {unreadAlerts > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-white">
                      {unreadAlerts > 9 ? "9+" : unreadAlerts}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: "10px", fontWeight: "bold" }}>Alerts</span>
              </div>

              <div onClick={() => navigate("/GlobalSearch")} className="flex flex-col items-center justify-center cursor-pointer text-white">
                <div className="flex items-center justify-center w-10 h-10 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition shadow-md border border-white/30 mb-1">
                  <FaSearch size={16} />
                </div>
                <span style={{ fontSize: "10px", fontWeight: "bold" }}>Search</span>
              </div>
            </div>
          ) : (
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-yellow-50 font-bold hover:bg-white/20 transition-all shadow-md">
              <FaArrowLeft size={14} className="text-yellow-400" />
              <span className="text-xs md:text-sm tracking-wider">BACK</span>
            </button>
          )}

          <div className="flex-grow"></div>

          {isHome ? (
            <div className="flex items-center gap-3">
              <div
                onClick={() => isSuperAdmin ? navigate("/admin-dashboard") : navigate("/UserProfile")}
                className="flex flex-col items-center justify-center cursor-pointer text-white"
              >
                <div className="flex items-center justify-center w-10 h-10 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition shadow-md border border-white/30 mb-1 overflow-hidden">
                  {isSuperAdmin
                    ? <span className="text-sm">🛠️</span>
                    : userPhoto
                      ? <img src={userPhoto} alt="Profile" className="w-full h-full object-cover" />
                      : <FaUserCircle size={22} className="text-white" />
                  }
                </div>
                <span style={{ fontSize: "10px", fontWeight: "bold" }}>
                  {isSuperAdmin ? "Admin" : "Profile"}
                </span>
              </div>

              <div className="relative" ref={dropdownRef}>
                <div onClick={() => setShowDropdown(!showDropdown)} className="flex flex-col items-center justify-center cursor-pointer text-white relative">
                  <div className="flex items-center justify-center w-10 h-10 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition shadow-md border border-white/30 mb-1 relative">
                    <FaBars size={18} />
                    {unreadCount > 0 && !isSuperAdmin && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border border-white">{unreadCount > 9 ? "9+" : unreadCount}</span>
                    )}
                  </div>
                  <span style={{ fontSize: "10px", fontWeight: "bold" }}>Menu</span>
                </div>

                {showDropdown && (
                  <div className="absolute top-[60px] right-0 bg-white w-56 rounded-xl shadow-2xl z-[100] overflow-hidden border border-gray-100">
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Menu</span>
                      <button onClick={() => setShowDropdown(false)} className="text-gray-400 hover:text-gray-700 transition p-1">
                        <FaTimes size={14} />
                      </button>
                    </div>
                    <div
                      onClick={() => { setShowDropdown(false); isSuperAdmin ? navigate("/admin-dashboard") : navigate("/UserProfile"); }}
                      className="p-4 flex items-center gap-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 bg-blue-50/30"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 border-2 border-blue-400 flex items-center justify-center">
                        {isSuperAdmin
                          ? <span className="text-sm">🛠️</span>
                          : userPhoto
                            ? <img src={userPhoto} alt="Profile" className="w-full h-full object-cover" />
                            : <FaUserCircle size={24} className="text-gray-400" />
                        }
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-800">{isSuperAdmin ? "Admin Dashboard" : "My Profile"}</div>
                        <div className="text-[10px] text-gray-500">View and Edit</div>
                      </div>
                    </div>
                    <div onClick={goToNotifications} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaBell className="text-orange-500" /> Notifications
                      {unreadCount > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{unreadCount}</span>}
                    </div>
                    <div onClick={() => { setShowDropdown(false); navigate("/Subscription"); }} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaCrown className="text-yellow-500" /> Upgrade to VIP (Pro)
                    </div>
                    <div onClick={() => { setShowDropdown(false); navigate("/RateAndRefer"); }} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaStar className="text-green-500" /> Rate & Refer
                    </div>
                    <div onClick={() => { setShowDropdown(false); navigate("/change-password"); }} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaLock className="text-blue-500" /> पासवर्ड बदलें
                    </div>
                    <div onClick={() => { setShowDropdown(false); navigate("/LegalAndPolicies"); }} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaShieldAlt className="text-gray-500" /> नियम एवं नीतियां
                    </div>
                    <div onClick={() => { setShowDropdown(false); navigate("/HelpAndSupport") }} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 text-sm font-bold text-gray-700">
                      <FaHeadset className="text-purple-500" /> सहायता एवं सपोर्ट
                    </div>
                    <div onClick={handleLogout} className="p-3 flex items-center gap-3 cursor-pointer bg-red-50 hover:bg-red-100 text-sm font-bold text-red-600 border-t border-red-100">
                      <FaSignOutAlt /> Logout
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button onClick={() => navigate("/")} className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-yellow-50 font-bold hover:bg-white/20 transition-all shadow-md">
              <FaHome size={16} className="text-yellow-400" />
              <span className="text-xs md:text-sm tracking-wider">HOME</span>
            </button>
          )}
        </div>
      </div>

      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center relative">
            <button onClick={() => setShowLogoutModal(false)} className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 transition">
              <FaTimes size={16} />
            </button>
            <div className="text-5xl mb-3">👋</div>
            <h3 className="text-xl font-black text-gray-800 mb-2">लॉगआउट करें?</h3>
            <p className="text-sm text-gray-500 font-medium mb-6">क्या आप सच में लॉगआउट करना चाहते हैं?</p>
            <div className="flex gap-3">
              <button onClick={confirmLogout} className="flex-1 py-2.5 rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 transition shadow-md">
                Logout
              </button>
              <button onClick={() => setShowLogoutModal(false)} className="flex-1 py-2.5 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AppHeader;